import { c as _c } from "react/compiler-runtime";
import { createContext, use, useEffect, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import { ShipmentAllocation } from "@/components/inventory/shipment-picker";
import { useShipmentPickerContext } from "@/components/inventory/shipment-picker/shipment-picker";
import { useLocations } from "@/hooks/use-locations";
import { Shipment } from "@/types/inventory/shipment";
import { DoseComponentFormValues, DoseFormValues } from "./dose-form-schema";
import { DoseLotSelectionAction, doseAllocationsUnchanged, getDoseDisplayedAllocations, getDoseInitialAllocations, getDoseLotCapabilities, getDoseReceiptIds, getRecordedDoseLots } from "./dose-lot-picker-model";
import { parseDoseLotReferences } from "./dose-lot-reference-options";
import { doseAllocationDecisionKey } from "./dose-lot-resolution";
import { DoseStockCoverage, getDoseStockCoverage, inferDoseStockAdministrationQuotas, roundPhysicalQuantity, sumAdministrationCounts } from "./dose-stock-planning";
import { DoseComponentInventoryEntry } from "./use-dose-component-inventory";
import { ShipmentLot } from "@/query/medications/medications";
interface DoseLotRow {
  id: string;
  label: string;
  shipment?: ShipmentLot;
  quantityShipment?: Shipment;
  disabled: boolean;
}
interface DoseLotPickerContextValue {
  state: {
    selected: DoseLotRow[];
    available: DoseLotRow[];
    references: string[];
    referenceChoices: Array<{
      code: string;
      selected: boolean;
    }>;
    search: string;
    showDepleted: boolean;
    pendingReference: boolean;
    notice?: string;
    stockCoverage: DoseStockCoverage[];
    coverageMessage?: string;
  };
  actions: {
    select: (id: string) => void;
    remove: (id: string) => void;
    addReference: (code: string) => void;
    toggleReference: (code: string) => void;
    setPendingReference: (pending: boolean) => void;
    setShowDepleted: (show: boolean) => void;
    save: (selected: ShipmentAllocation[]) => void;
    close: () => void;
  };
  meta: {
    inventory: DoseComponentInventoryEntry;
    medicationId?: string;
    medicationName: string;
    locationNames: Record<string, string>;
    listId: string;
    canSelect: boolean;
    canDiscoverDepleted: boolean;
    drawsInventory: boolean;
    correctsInventory: boolean;
    saveDisabled: boolean;
    allowsEmptySelection: boolean;
  };
}
const DoseLotPickerContext = createContext<DoseLotPickerContextValue | null>(null);
export function useDoseLotPicker() {
  const context = use(DoseLotPickerContext);
  if (!context) throw new Error("Dose lot content requires a DoseLotPicker session");
  return context;
}

/** One draft boundary. Physical quantities stay owned by ShipmentPicker, never mirrored. */
export function DoseLotPickerProvider(t0) {
  const $ = _c(105);
  const {
    index,
    component,
    inventory,
    close,
    children
  } = t0;
  const {
    setValue
  } = useFormContext();
  const picker = useShipmentPickerContext();
  const {
    allLocationNameById
  } = useLocations();
  let t1;
  if ($[0] !== component || $[1] !== inventory) {
    t1 = getDoseLotCapabilities(component, inventory);
    $[0] = component;
    $[1] = inventory;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const {
    action
  } = t1;
  const drawsInventory = action === DoseLotSelectionAction.DRAW;
  const recordsReceipts = action === DoseLotSelectionAction.RECORD;
  let t2;
  if ($[3] !== component) {
    t2 = () => getDoseReceiptIds(component);
    $[3] = component;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const [receiptIds, setReceiptIds] = useState(t2);
  let t3;
  if ($[5] !== component) {
    t3 = new Set(getDoseReceiptIds(component));
    $[5] = component;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const hydratedReceiptIds = useRef(t3);
  let t4;
  let t5;
  if ($[7] !== component || $[8] !== recordsReceipts) {
    t4 = () => {
      if (!recordsReceipts) {
        return;
      }
      const arriving = getDoseReceiptIds(component).filter(id => !hydratedReceiptIds.current.has(id));
      if (!arriving.length) {
        return;
      }
      arriving.forEach(id_0 => hydratedReceiptIds.current.add(id_0));
      setReceiptIds(previous => Array.from(new Set([...previous, ...arriving])));
    };
    t5 = [component, recordsReceipts];
    $[7] = component;
    $[8] = recordsReceipts;
    $[9] = t4;
    $[10] = t5;
  } else {
    t4 = $[9];
    t5 = $[10];
  }
  useEffect(t4, t5);
  let t6;
  if ($[11] !== component.lotSelection?.value) {
    t6 = () => parseDoseLotReferences(component.lotSelection?.value).map(_temp);
    $[11] = component.lotSelection?.value;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const [referenceChoices, setReferenceChoices] = useState(t6);
  let t7;
  if ($[13] !== referenceChoices) {
    t7 = referenceChoices.filter(_temp2).map(_temp3);
    $[13] = referenceChoices;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const references = t7;
  const [pendingReference, setPendingReference] = useState(false);
  const [notice, setNotice] = useState();
  let T0;
  let coverageMessage;
  let remove;
  let save;
  let saveDisabled;
  let select;
  let stockCoverage;
  let t8;
  let t9;
  let unresolvedMessage;
  if ($[15] !== close || $[16] !== component || $[17] !== drawsInventory || $[18] !== index || $[19] !== inventory || $[20] !== pendingReference || $[21] !== picker || $[22] !== receiptIds || $[23] !== recordsReceipts || $[24] !== references || $[25] !== setValue) {
    const baseline = getRecordedDoseLots(component);
    const unresolvedAllocations = drawsInventory ? getDoseDisplayedAllocations(component, inventory).filter(allocation => !picker.shipments.some(shipment => shipment.id === allocation.shipmentId)) : [];
    unresolvedMessage = unresolvedAllocations.length ? "Recorded inventory lots are unavailable. Refresh before changing their amounts." : undefined;
    let physicalAllocations;
    let t10;
    if ($[36] !== component || $[37] !== drawsInventory || $[38] !== inventory || $[39] !== picker.allocations || $[40] !== picker.shipments || $[41] !== references) {
      physicalAllocations = Object.values(picker.allocations).map(_temp4);
      stockCoverage = drawsInventory ? getDoseStockCoverage(physicalAllocations, inventory.preview?.stocks ?? [], component.quantity) : [];
      const unchangedHistory = doseAllocationsUnchanged(Object.values(picker.allocations).map(_temp5), getDoseInitialAllocations(component, inventory, picker.shipments)) && (component.checkoutReuse || component.id && inventory.preview?.ordinaryEditMovesInventory === false);
      const resumesAutomaticDraw = component.lotAllocations === undefined && physicalAllocations.length === 0 && references.length === 0 && parseDoseLotReferences(component.lotSelection?.value).length > 0 && inventory.preview?.ordinaryEditMovesInventory === true && !inventory.preview.ownedLots.some(_temp6);
      t10 = !unchangedHistory && !resumesAutomaticDraw && stockCoverage.some(coverage => !Number.isFinite(coverage.deltaPhysical) || coverage.deltaPhysical > 0 || references.length === 0 && coverage.deltaPhysical !== 0);
      $[36] = component;
      $[37] = drawsInventory;
      $[38] = inventory;
      $[39] = picker.allocations;
      $[40] = picker.shipments;
      $[41] = references;
      $[42] = physicalAllocations;
      $[43] = stockCoverage;
      $[44] = t10;
    } else {
      physicalAllocations = $[42];
      stockCoverage = $[43];
      t10 = $[44];
    }
    const invalidCoverage = t10;
    coverageMessage = invalidCoverage ? references.length > 0 ? "Do not exceed the required amount for any stock." : "Assign the full required amount for every stock." : undefined;
    saveDisabled = !!coverageMessage || pendingReference || inventory.isCheckingInventory || !!unresolvedMessage || !!inventory.previewError;
    const selectedIds = drawsInventory ? Array.from(new Set([...Object.keys(picker.allocations), ...unresolvedAllocations.map(_temp7)])) : recordsReceipts ? receiptIds : Array.from(new Set([...receiptIds, ...getDoseDisplayedAllocations(component, inventory).map(_temp8)]));
    const byId = new Map(inventory.shipments.map(_temp9));
    const rowFor = id_1 => ({
      id: id_1,
      label: byId.get(id_1)?.lotNumber || baseline.find(lot_1 => lot_1.shipmentId === id_1)?.lotNumber || "Unlabeled lot",
      shipment: byId.get(id_1),
      quantityShipment: drawsInventory ? picker.allocations[id_1]?.shipment : undefined,
      disabled: drawsInventory && !picker.isSelected(id_1) && (() => {
        const shipment_0 = picker.shipments.find(lot_2 => lot_2.id === id_1);
        return !shipment_0 || picker.getCapacity(shipment_0) <= 0 && !picker.canUseExtraYield(shipment_0);
      })()
    });
    const candidates = drawsInventory ? picker.filteredShipments.map(lot_3 => byId.get(lot_3.id)).filter(_temp10) : recordsReceipts ? inventory.shipments.filter(lot_5 => (lot_5.remainingQuantity > 0 || picker.showDepleted) && `${lot_5.lotNumber ?? ""} ${lot_5.supplierName ?? ""}`.toLowerCase().includes(picker.searchTerm.trim().toLowerCase())) : [];
    select = id_2 => {
      if (drawsInventory) {
        const lot_6 = picker.shipments.find(candidate => candidate.id === id_2);
        if (!lot_6) {
          const matching = byId.get(id_2);
          setNotice(matching && picker.shipments.some(candidate_0 => candidate_0.stockId === matching.stockId) ? "Actual inventory corrections can use only this dose's originally recorded lots." : "This lot cannot be used with the current dose conversion. Choose another lot.");
          return;
        }
        setNotice(undefined);
        if (!picker.isSelected(id_2)) {
          const stock = inventory.preview?.stocks.find(entry => entry.stockId === lot_6.stockId);
          if (!stock) {
            return;
          }
          const sameStockPhysical = sumAdministrationCounts(physicalAllocations.filter(allocation_3 => allocation_3.stockId === lot_6.stockId).map(_temp11));
          const remaining = Math.max(0, roundPhysicalQuantity(component.quantity * stock.perAdministration - sameStockPhysical));
          const capacity = picker.getCapacity(lot_6);
          picker.toggleShipment(lot_6);
          picker.setQuantity(id_2, capacity <= 0 && picker.canUseExtraYield(lot_6) ? remaining : Math.min(capacity, remaining));
        }
      } else {
        if (recordsReceipts) {
          setNotice(undefined);
          setReceiptIds(previous_0 => previous_0.includes(id_2) ? previous_0 : [...previous_0, id_2]);
        } else {
          setNotice("Inventory use stays unchanged here. Use Correct actual inventory use to change a recorded inventory lot.");
        }
      }
    };
    let t11;
    if ($[45] !== drawsInventory || $[46] !== picker || $[47] !== recordsReceipts) {
      t11 = id_3 => {
        setNotice(undefined);
        if (drawsInventory) {
          const lot_7 = picker.allocations[id_3]?.shipment;
          if (lot_7) {
            picker.toggleShipment(lot_7);
          }
        } else {
          if (recordsReceipts) {
            hydratedReceiptIds.current.add(id_3);
            setReceiptIds(previous_1 => previous_1.filter(value => value !== id_3));
          }
        }
      };
      $[45] = drawsInventory;
      $[46] = picker;
      $[47] = recordsReceipts;
      $[48] = t11;
    } else {
      t11 = $[48];
    }
    remove = t11;
    let t12;
    if ($[49] !== close || $[50] !== component || $[51] !== drawsInventory || $[52] !== index || $[53] !== inventory || $[54] !== picker.shipments || $[55] !== receiptIds || $[56] !== recordsReceipts || $[57] !== references || $[58] !== saveDisabled || $[59] !== setValue) {
      t12 = selected => {
        if (saveDisabled) {
          return;
        }
        const lotSelection = references.length ? {
          type: "lot" as const,
          value: references.join("\n")
        } : null;
        if (drawsInventory && inventory.preview) {
          if (component.singleLotSelection !== undefined) {
            setValue(`components.${index}.singleLotSelection`, undefined, {
              shouldDirty: true,
              shouldValidate: true
            });
          }
          const initial = getDoseInitialAllocations(component, inventory, picker.shipments);
          const unchanged = doseAllocationsUnchanged(selected, initial);
          const next = selected.map(_temp12);
          const quotas = inferDoseStockAdministrationQuotas(next, inventory.preview.stocks, component.quantity);
          const key = doseAllocationDecisionKey({
            ...component,
            lotSelection,
            lotAllocations: next,
            stockAdministrationQuotas: quotas
          }, inventory);
          for (const allocation_6 of next) {
            if (allocation_6.allowExtraYield) {
              allocation_6.decisionKey = JSON.stringify([key, allocation_6.quantity]);
            }
          }
          if (!unchanged || component.lotAllocations !== undefined && !component.checkoutReuse) {
            setValue(`components.${index}.stockAdministrationQuotas`, quotas, {
              shouldDirty: true,
              shouldValidate: true
            });
          }
          if (!unchanged) {
            setValue(`components.${index}.lotAllocationBasis`, undefined, {
              shouldDirty: true
            });
            setValue(`components.${index}.lotAllocations`, next, {
              shouldDirty: true,
              shouldValidate: true
            });
            setValue(`components.${index}.checkoutReuse`, false, {
              shouldDirty: true
            });
          }
        } else {
          if (recordsReceipts && JSON.stringify(receiptIds) !== JSON.stringify(getDoseReceiptIds(component))) {
            setValue(`components.${index}.clinicalShipmentIds`, receiptIds, {
              shouldDirty: true,
              shouldValidate: true
            });
          }
        }
        setValue(`components.${index}.lotSelection`, lotSelection, {
          shouldDirty: true,
          shouldValidate: true
        });
        close();
      };
      $[49] = close;
      $[50] = component;
      $[51] = drawsInventory;
      $[52] = index;
      $[53] = inventory;
      $[54] = picker.shipments;
      $[55] = receiptIds;
      $[56] = recordsReceipts;
      $[57] = references;
      $[58] = saveDisabled;
      $[59] = setValue;
      $[60] = t12;
    } else {
      t12 = $[60];
    }
    save = t12;
    T0 = DoseLotPickerContext;
    t8 = selectedIds.map(rowFor);
    t9 = candidates.filter(lot_8 => !selectedIds.includes(lot_8.id)).map(lot_9 => rowFor(lot_9.id));
    $[15] = close;
    $[16] = component;
    $[17] = drawsInventory;
    $[18] = index;
    $[19] = inventory;
    $[20] = pendingReference;
    $[21] = picker;
    $[22] = receiptIds;
    $[23] = recordsReceipts;
    $[24] = references;
    $[25] = setValue;
    $[26] = T0;
    $[27] = coverageMessage;
    $[28] = remove;
    $[29] = save;
    $[30] = saveDisabled;
    $[31] = select;
    $[32] = stockCoverage;
    $[33] = t8;
    $[34] = t9;
    $[35] = unresolvedMessage;
  } else {
    T0 = $[26];
    coverageMessage = $[27];
    remove = $[28];
    save = $[29];
    saveDisabled = $[30];
    select = $[31];
    stockCoverage = $[32];
    t8 = $[33];
    t9 = $[34];
    unresolvedMessage = $[35];
  }
  const t10 = notice ?? unresolvedMessage;
  let t11;
  if ($[61] !== coverageMessage || $[62] !== pendingReference || $[63] !== picker.searchTerm || $[64] !== picker.showDepleted || $[65] !== referenceChoices || $[66] !== references || $[67] !== stockCoverage || $[68] !== t10 || $[69] !== t8 || $[70] !== t9) {
    t11 = {
      selected: t8,
      available: t9,
      references,
      referenceChoices,
      search: picker.searchTerm,
      showDepleted: picker.showDepleted,
      pendingReference,
      notice: t10,
      stockCoverage,
      coverageMessage
    };
    $[61] = coverageMessage;
    $[62] = pendingReference;
    $[63] = picker.searchTerm;
    $[64] = picker.showDepleted;
    $[65] = referenceChoices;
    $[66] = references;
    $[67] = stockCoverage;
    $[68] = t10;
    $[69] = t8;
    $[70] = t9;
    $[71] = t11;
  } else {
    t11 = $[71];
  }
  let t12;
  let t13;
  if ($[72] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = code_0 => setReferenceChoices(previous_2 => {
      const existing = previous_2.some(choice_1 => choice_1.code.toLowerCase() === code_0.toLowerCase());
      return existing ? previous_2.map(choice_2 => choice_2.code.toLowerCase() === code_0.toLowerCase() ? {
        ...choice_2,
        selected: true
      } : choice_2) : [...previous_2, {
        code: code_0,
        selected: true
      }];
    });
    t13 = code_1 => setReferenceChoices(previous_3 => previous_3.map(choice_3 => choice_3.code === code_1 ? {
      ...choice_3,
      selected: !choice_3.selected
    } : choice_3));
    $[72] = t12;
    $[73] = t13;
  } else {
    t12 = $[72];
    t13 = $[73];
  }
  let t14;
  if ($[74] !== close || $[75] !== picker.setShowDepleted || $[76] !== remove || $[77] !== save || $[78] !== select) {
    t14 = {
      select,
      remove,
      addReference: t12,
      toggleReference: t13,
      setPendingReference,
      setShowDepleted: picker.setShowDepleted,
      save,
      close
    };
    $[74] = close;
    $[75] = picker.setShowDepleted;
    $[76] = remove;
    $[77] = save;
    $[78] = select;
    $[79] = t14;
  } else {
    t14 = $[79];
  }
  const t15 = inventory.selectedMedication?.name ?? component.name;
  const t16 = drawsInventory || recordsReceipts;
  const t17 = drawsInventory || recordsReceipts;
  const t18 = component.correctActualInventoryUse === true;
  let t19;
  if ($[80] !== component || $[81] !== inventory || $[82] !== referenceChoices.length || $[83] !== references) {
    t19 = !component.correctActualInventoryUse && (references.length > 0 || referenceChoices.length > 0 && getDoseDisplayedAllocations(component, inventory).length === 0);
    $[80] = component;
    $[81] = inventory;
    $[82] = referenceChoices.length;
    $[83] = references;
    $[84] = t19;
  } else {
    t19 = $[84];
  }
  let t20;
  if ($[85] !== allLocationNameById || $[86] !== component.medicationId || $[87] !== drawsInventory || $[88] !== inventory || $[89] !== picker.listId || $[90] !== saveDisabled || $[91] !== t15 || $[92] !== t16 || $[93] !== t17 || $[94] !== t18 || $[95] !== t19) {
    t20 = {
      inventory,
      medicationId: component.medicationId,
      medicationName: t15,
      locationNames: allLocationNameById,
      listId: picker.listId,
      canSelect: t16,
      canDiscoverDepleted: t17,
      drawsInventory,
      correctsInventory: t18,
      saveDisabled,
      allowsEmptySelection: t19
    };
    $[85] = allLocationNameById;
    $[86] = component.medicationId;
    $[87] = drawsInventory;
    $[88] = inventory;
    $[89] = picker.listId;
    $[90] = saveDisabled;
    $[91] = t15;
    $[92] = t16;
    $[93] = t17;
    $[94] = t18;
    $[95] = t19;
    $[96] = t20;
  } else {
    t20 = $[96];
  }
  let t21;
  if ($[97] !== t11 || $[98] !== t14 || $[99] !== t20) {
    t21 = {
      state: t11,
      actions: t14,
      meta: t20
    };
    $[97] = t11;
    $[98] = t14;
    $[99] = t20;
    $[100] = t21;
  } else {
    t21 = $[100];
  }
  let t22;
  if ($[101] !== T0 || $[102] !== children || $[103] !== t21) {
    t22 = <T0 value={t21}>{children}</T0>;
    $[101] = T0;
    $[102] = children;
    $[103] = t21;
    $[104] = t22;
  } else {
    t22 = $[104];
  }
  return t22;
}
function _temp12(allocation_5) {
  return {
    shipmentId: allocation_5.shipmentId,
    stockId: allocation_5.shipment.stockId,
    quantity: allocation_5.quantity,
    ...(allocation_5.allowExtraYield && {
      allowExtraYield: true,
      decisionKey: allocation_5.decisionKey
    })
  };
}
function _temp11(allocation_4) {
  return allocation_4.quantity;
}
function _temp10(lot_4) {
  return !!lot_4;
}
function _temp9(lot_0) {
  return [lot_0.id, lot_0];
}
function _temp8(lot) {
  return lot.shipmentId;
}
function _temp7(allocation_2) {
  return allocation_2.shipmentId;
}
function _temp6(owned) {
  return owned.outstanding > 0;
}
function _temp5(allocation_1) {
  return {
    ...allocation_1,
    shipmentId: allocation_1.shipment.id
  };
}
function _temp4(allocation_0) {
  return {
    shipmentId: allocation_0.shipment.id,
    stockId: allocation_0.shipment.stockId,
    quantity: allocation_0.quantity
  };
}
function _temp3(choice_0) {
  return choice_0.code;
}
function _temp2(choice) {
  return choice.selected;
}
function _temp(code) {
  return {
    code,
    selected: true
  };
}